"use client";

import { useState } from "react";
import { Zustandspille } from "../zustandspille";
import { feldIds, useFeldId } from "./feld";

/**
 * Tag-Wahl (Brötchen-Vorbestellung B1, Spec 2026-10-20, Design-Guide `tag-wahl.md`): mehrere Tage einer Woche als Umschalter wählen
 * (Serie von Ausgabefenstern: Mo, Di, Mi …). Gebaut als `fieldset` mit benannter Legende; jeder Tag ist ein Kontrollkästchen, das mit
 * `sr-only` verborgen ist und per Tastatur erreichbar bleibt (Tab, Leertaste), der Fokus liegt als Rahmen am Etikett
 * (`has-focus-visible`). Die Quellblöcke verstecken die Eingabe mit `hidden`: dann gäbe es keine Tastaturbedienung (K5).
 * Der Wert ist die Liste der gewählten Tage (kontrolliert), die Reihenfolge bestimmt `optionen`. Pflicht bleibt unmarkiert (F5), der
 * Feldfehler steht als Pille unter der Gruppe (r100, AW-005). Der Baustein prüft keine Rechte und kennt keine Wochentage: der Aufrufer
 * liefert Wert, Kurzform (sichtbar) und Langform (Name für Hilfstechnik).
 *
 * Verallgemeinert (M-153 AW-1, Rollen-Chips „Sichtbar für“): der Wert darf Zahl oder Text sein (Rollenkennung); Kurz- und Langform
 * dürfen gleich sein (voller Rollenname). `mindestens` macht die Auswahl zur Pflicht: ist sie leer, steht der Satz als Feldfehler am
 * Feld (r100), sobald die Person die Auswahl angefasst hat oder der Aufrufer `fehler` setzt; ein gesetzter `fehler` hat Vorrang. Der
 * Baustein bietet nur an, was der Aufrufer liefert: welche Optionen eine Person vergeben darf, filtert der Aufrufer still (r110).
 */
export interface TagWahlOption<W extends number | string = number> {
  wert: W;
  /** Sichtbare Kurzform („Mo“). */
  kurz: string;
  /** Name für Hilfstechnik („Montag“). */
  lang: string;
}

export function TagWahl<W extends number | string = number>({
  id: idVorgabe,
  legende,
  name,
  optionen,
  wert,
  beiAenderung,
  hilfe,
  fehler,
  mindestens,
  deaktiviert = false,
}: {
  id?: string;
  legende: string;
  name: string;
  optionen: readonly TagWahlOption<W>[];
  /** Die gewählten Optionen (kontrolliert). */
  wert: readonly W[];
  /** Neue Auswahl in der Reihenfolge von `optionen`. */
  beiAenderung: (neu: W[]) => void;
  hilfe?: string;
  /** Feldfehler (F3): kurzer Satz, durch erneute Eingabe behebbar. */
  fehler?: string;
  /** Pflicht „mindestens eine“: Satz am Feld, solange nichts gewählt ist (erscheint, sobald die Auswahl angefasst wurde). */
  mindestens?: string;
  deaktiviert?: boolean;
}) {
  const id = useFeldId(idVorgabe);
  const ids = feldIds(id);
  const [angefasst, setAngefasst] = useState(false);
  const pflichtSatz = mindestens !== undefined && wert.length === 0 && angefasst ? mindestens : undefined;
  const fehlerText = fehler ?? pflichtSatz;
  const beschreibung = [hilfe ? ids.hilfe : null, fehlerText ? ids.fehler : null].filter(Boolean).join(" ") || undefined;
  const umschalten = (tag: W, an: boolean) => {
    setAngefasst(true);
    const neu = an ? [...wert, tag] : wert.filter((t) => t !== tag);
    beiAenderung(optionen.map((o) => o.wert).filter((w) => neu.includes(w)));
  };
  return (
    <>
      {/* Preline: productivity/productivity-sub-calendars-scheduling/productivity-cat-scheduling-modals/custom-recurrence-modal-with-repeat-rules (nur die Tageswahl als Herkunft; Kontrollkästchen mit `sr-only` statt `hidden`, Fokusrahmen am Etikett, K5) */}
      <fieldset aria-describedby={beschreibung} aria-invalid={fehlerText ? true : undefined} className="min-w-0">
        <legend className="mb-2 block text-sm font-medium text-foreground">{legende}</legend>
        <div className="flex flex-wrap gap-2">
          {optionen.map((o) => (
            <label
              key={o.wert}
              htmlFor={`${id}-${o.wert}`}
              className="relative inline-flex min-w-11 h-10 px-3 items-center justify-center rounded-lg cursor-pointer text-sm text-foreground bg-layer ring-1 ring-layer-line hover:bg-muted-hover has-checked:bg-primary has-checked:text-primary-foreground has-checked:ring-primary has-focus-visible:ring-2 has-focus-visible:ring-primary-focus has-disabled:opacity-50 has-disabled:cursor-default"
            >
              <input
                id={`${id}-${o.wert}`}
                type="checkbox"
                name={name}
                value={o.wert}
                className="sr-only"
                aria-label={o.lang}
                checked={wert.includes(o.wert)}
                disabled={deaktiviert}
                onChange={(e) => umschalten(o.wert, e.target.checked)}
              />
              <span aria-hidden="true">{o.kurz}</span>
            </label>
          ))}
        </div>
        {hilfe && (
          <p id={ids.hilfe} className="mt-2 text-sm text-muted-foreground-1">
            {hilfe}
          </p>
        )}
        {fehlerText && (
          <p id={ids.fehler} className="mt-2">
            {/* Abweichung AW-005: Feldfehler V2, Satz in Pille */}
            <Zustandspille ton="gefahr" mitPunkt>{fehlerText}</Zustandspille>
          </p>
        )}
      </fieldset>
    </>
  );
}
